import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    computed,
    inject,
    input,
    output,
} from '@angular/core';
import { Store } from '@ngrx/store';

import { ProductCardModel } from '../../models/media.models';
import { MediaCarouselComponent } from '../media-carousel/media-carousel.component';
import { MediaImageComponent } from '../media-image/media-image.component';
import { MediaSkeletonComponent } from '../media-skeleton/media-skeleton.component';
import { MediaVideoComponent } from '../media-video/media-video.component';

import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import { MediasPublicModels } from '@features/products/medias';
import { ProductPublicModels } from '@features/products/product';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { ProductClickDirective } from '@shared/media/directives/product-click.directive';

@Component({
    selector: 'product-card',
    templateUrl: './product-card.component.html',
    imports: [
        MediaImageComponent,
        MediaVideoComponent,
        MediaSkeletonComponent,
        MediaCarouselComponent,
        NgTemplateOutlet,
        ProductClickDirective,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCardComponent implements OnInit {
    private readonly store = inject(Store);

    private readonly coreStorageService: CoreStorageService = inject(CoreStorageService);

    public showVideo = false;

    public imageLoaded = false;

    public imageCarouselLoaded = false;

    public videoLoaded = false;

    // INPUT DATA
    public imageUrl = input.required<string>();

    public productImages = input<MediasPublicModels.PublicMediaResolved[]>();

    public config = input.required<ProductCardModel>();

    public videoUrl = input<string>();

    public showMediaCarousel = computed(() => {
        // eslint-disable-next-line max-len
        return this.productImages()?.length > 1 && this.config().showMediaCarousel && this.config().clickable && !this.videoUrl();
    });

    public linkHref = input<string>();

    public hasInView = input<boolean>();

    public bannerLinks = input<ProductPublicModels.PublicBannerLink[]>();

    public animationCarousel: any = false;

    private readonly executedAnimationKey = 'mediaCarouselExecutedAnimation'; // Storage key to know if the initial animation has already been executed

    private readonly resolution: Resolutions = this.store.selectSignal(getResolution)();

    // OUTPUT EVENTS
    public readonly mediaError = output<Event>();

    public readonly clickMedia = output<Event>();

    public ngOnInit(): void {
        if (!this.imageUrl() || this.config()?.media?.imagePriority) {
            this.imageLoaded = true;
        }

        if (this.showMediaCarousel()) {
            const animationExecuted = this.coreStorageService.getValue(this.executedAnimationKey);
            if (!animationExecuted) {
                this.animationCarousel = true;
                this.coreStorageService.setValue(this.executedAnimationKey, true, 'PERMANENT', 'TECHNICAL');
            }
        }
    }

    public onVideoLoad() {
        this.videoLoaded = true;
    }

    public onImageError(event) {
        this.mediaError.emit(event);

        this.imageLoaded = true;
        if (this.videoUrl()) {
            this.showVideo = true;
        }
    }

    public onClickMedia(event) {
        this.clickMedia.emit(event);
        event?.preventDefault();
    }

    public onImageLoad(_event) {
        this.imageLoaded = true;
        this.showVideo = true;
    }

    public onImageCarouselLoad(_event) {
        this.imageCarouselLoaded = true;
    }

    public handleKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') {
            this.onClickMedia(event);
            event.preventDefault();
        }
    }

    public preventNavigation(event: Event) {
        event.preventDefault();
    }

    public isMobile(): boolean {
        return [Resolutions.MOBILE, Resolutions.TABLET].includes(this.resolution);
    }
}
